import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import QuickPinchZoom, { make3dTransformValue } from 'react-quick-pinch-zoom';

interface FullScreenImageViewerProps {
  src: string;
  trigger?: React.ReactNode;
}

export function FullScreenImageViewer({ src, trigger }: FullScreenImageViewerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const pinchZoomRef = useRef<QuickPinchZoom>(null);

  const onUpdate = useCallback(({ x, y, scale }: { x: number; y: number; scale: number }) => {
    if (imgRef.current) {
      const value = make3dTransformValue({ x, y, scale });
      imgRef.current.style.setProperty('transform', value);
    }
  }, []);

  const handleZoomIn = () => {
    pinchZoomRef.current?.scaleTo({
      scale: (pinchZoomRef.current?.scale || 1) + 0.5,
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
  };

  const handleZoomOut = () => {
    pinchZoomRef.current?.scaleTo({
      scale: Math.max((pinchZoomRef.current?.scale || 1) - 0.5, 1),
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
  };

  const handleReset = () => {
    pinchZoomRef.current?.alignCenter({ animate: true });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <div className="relative group cursor-pointer overflow-hidden rounded-md border bg-muted h-full w-full">
            <img 
              src={src} 
              alt="Score Sheet" 
              className="h-full w-full object-cover transition-transform group-hover:scale-105" 
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
              <ZoomIn className="h-6 w-6 text-white" />
            </div>
          </div>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-full w-full h-full p-0 overflow-hidden bg-black/95 border-none sm:rounded-none">
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-[60]">
          <div className="flex gap-2">
            <Button 
              size="icon" 
              variant="ghost" 
              className="text-white hover:bg-white/20 bg-black/20 backdrop-blur-sm"
              onClick={handleZoomIn}
            >
              <ZoomIn className="h-6 w-6" />
            </Button>
            <Button 
              size="icon" 
              variant="ghost" 
              className="text-white hover:bg-white/20 bg-black/20 backdrop-blur-sm"
              onClick={handleZoomOut}
            >
              <ZoomOut className="h-6 w-6" />
            </Button>
            <Button 
              size="icon" 
              variant="ghost" 
              className="text-white hover:bg-white/20 bg-black/20 backdrop-blur-sm"
              onClick={handleReset}
            >
              <RotateCcw className="h-5 w-5" />
            </Button>
          </div>
          <Button 
            size="icon" 
            variant="ghost" 
            className="text-white hover:bg-white/20 bg-black/20 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>

        <div className="w-full h-full flex items-center justify-center">
          <QuickPinchZoom
            ref={pinchZoomRef}
            onUpdate={onUpdate}
            wheelScaleFactor={1.1}
            draggableUnZoomed={false}
            inertia={true}
          >
            <img 
              ref={imgRef}
              src={src} 
              alt="Score Sheet Full" 
              className="max-w-full max-h-full object-contain select-none"
              onClick={(e) => e.stopPropagation()}
            />
          </QuickPinchZoom>
        </div>
      </DialogContent>
    </Dialog>
  );
}
